import { FormEvent, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { clearRedirectContext, navigatePostLogin, resolvePostLoginDestination } from '../lib/redirect';
import { BrandLogo } from '../components/BrandLogo';
import { PasswordField } from '../components/PasswordField';
import { CHALLENGE } from '../lib/constants';

/** Partner UI — signs in via POST /partner/login (JSON tokens, no OAuth redirect). */
export function PartnerSignInPage(): JSX.Element {
  const { partnerLogin, challenge, completePartnerNewPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(searchParams.get('error'));
  const [loading, setLoading] = useState(false);
  const notice = (location.state as { notice?: string } | null)?.notice ?? null;

  useEffect(() => {
    clearRedirectContext();
  }, []);

  async function finishLogin() {
    const destination = await resolvePostLoginDestination();
    navigatePostLogin(destination, navigate);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (challenge?.challenge === CHALLENGE.NEW_PASSWORD_REQUIRED) {
        await completePartnerNewPassword(email, newPassword);
        await finishLogin();
        return;
      }
      const result = await partnerLogin(email, password);
      if (result.challenge) {
        setError(null);
        return;
      }
      await finishLogin();
    } catch (submissionError) {
      setError(
        submissionError instanceof Error ? submissionError.message : 'Partner authentication failed',
      );
    } finally {
      setLoading(false);
    }
  }

  const needsNewPassword = challenge?.challenge === CHALLENGE.NEW_PASSWORD_REQUIRED;

  return (
    <div className="login-screen">
      <div className="login-card">
        <BrandLogo />
        <h1>Partner sign in</h1>
        <p className="login-card__copy">
          {needsNewPassword
            ? 'Set a permanent password to finish signing in.'
            : 'Sign in with your partner account. After authentication you stay signed in on this portal (API session). Partner apps use Core API tokens, not an authorization code.'}
        </p>

        <form className="login-form" onSubmit={handleSubmit}>
          <label>
            <span>Email</span>
            <input
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              type="email"
              required
              autoComplete="username"
              disabled={needsNewPassword}
            />
          </label>
          {needsNewPassword ? (
            <PasswordField
              label="New password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              required
            />
          ) : (
            <PasswordField
              label="Password"
              value={password}
              onChange={setPassword}
              autoComplete="current-password"
              required
            />
          )}
          {notice ? <p className="hint-text">{notice}</p> : null}
          {error ? <div className="form-error">{error}</div> : null}
          <button className="primary-button" type="submit" disabled={loading}>
            {loading ? 'Signing in…' : needsNewPassword ? 'Set password & continue' : 'Sign in'}
          </button>
        </form>

        <p className="hint-text">
          <Link className="text-link" to="/partner/forgot-password">
            Forgot password?
          </Link>
        </p>

        <p className="hint-text">
          HQ invite? Check your email for a <Link to="/partner/setup">setup link</Link>. Not a
          partner? <Link to="/login">General sign in</Link>
        </p>
      </div>
    </div>
  );
}
